import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaSearch, FaFire, FaArrowUp, FaGamepad } from 'react-icons/fa';

const Categories = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');

  // Mock category data
  const categories = [
    { id: 1, name: 'Apex Legends', viewers: 124500, channels: 420, image: 'apex-placeholder.jpg', tags: ['FPS', 'Battle Royale'] },
    { id: 2, name: 'Valorant', viewers: 98700, channels: 380, image: 'valorant-placeholder.jpg', tags: ['FPS', 'Tactical Shooter'] },
    { id: 3, name: 'Minecraft', viewers: 87300, channels: 1250, image: 'minecraft-placeholder.jpg', tags: ['Sandbox', 'Adventure'] },
    { id: 4, name: 'Fortnite', viewers: 153200, channels: 950, image: 'fortnite-placeholder.jpg', tags: ['Battle Royale', 'Building'] },
    { id: 5, name: 'Call of Duty: Warzone', viewers: 112800, channels: 520, image: 'cod-placeholder.jpg', tags: ['FPS', 'Battle Royale'] },
    { id: 6, name: 'Grand Theft Auto V', viewers: 176400, channels: 740, image: 'gta-placeholder.jpg', tags: ['Open World', 'Action'] },
    { id: 7, name: 'League of Legends', viewers: 224900, channels: 1100, image: 'lol-placeholder.jpg', tags: ['MOBA', 'Team-Based'] },
    { id: 8, name: 'Counter-Strike 2', viewers: 198600, channels: 880, image: 'cs2-placeholder.jpg', tags: ['FPS', 'Tactical Shooter'] },
    { id: 9, name: 'Dota 2', viewers: 145700, channels: 590, image: 'dota-placeholder.jpg', tags: ['MOBA', 'Team-Based'] },
    { id: 10, name: 'Elden Ring', viewers: 78600, channels: 340, image: 'elden-placeholder.jpg', tags: ['Action RPG', 'Open World'] },
    { id: 11, name: 'Among Us', viewers: 54300, channels: 290, image: 'among-placeholder.jpg', tags: ['Social Deduction', 'Party'] },
    { id: 12, name: 'Roblox', viewers: 89500, channels: 1850, image: 'roblox-placeholder.jpg', tags: ['Platform', 'Social'] }
  ];

  // Filter categories based on search query
  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Sort categories based on selected sort option
  const sortedCategories = [...filteredCategories].sort((a, b) => {
    if (sortBy === 'popular') {
      return b.viewers - a.viewers;
    } else if (sortBy === 'channels') {
      return b.channels - a.channels;
    } else if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-harmony-blue">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <motion.h1 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-3xl font-bold text-harmony-white mb-6"
        >
          Game Categories
        </motion.h1>
        
        {/* Search and filter section */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-harmony-white/50" />
            </div>
            <input
              type="text"
              placeholder="Search categories or tags..."
              className="w-full bg-harmony-blue-dark border border-harmony-white/10 text-harmony-white pl-10 pr-4 py-3 rounded-lg focus:outline-none focus:border-harmony-orange"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setSortBy('popular')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                sortBy === 'popular' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              <FaFire className="mr-2" /> Popular
            </button>
            <button 
              onClick={() => setSortBy('channels')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                sortBy === 'channels' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              <FaArrowUp className="mr-2" /> Most Channels
            </button>
            <button 
              onClick={() => setSortBy('name')} 
              className={`px-4 py-3 rounded-lg flex items-center ${
                sortBy === 'name' 
                  ? 'bg-harmony-orange text-harmony-white' 
                  : 'bg-harmony-blue-dark text-harmony-white/70 hover:bg-harmony-blue-dark/80'
              }`}
            >
              A-Z
            </button>
          </div>
        </div>
        
        {/* Categories grid */}
        {sortedCategories.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05 * (index % 12) }}
                className="bg-harmony-blue-dark rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1"
              >
                <Link to={`/browse?category=${encodeURIComponent(category.name)}`}>
                  <div className="aspect-video bg-harmony-black relative">
                    <div className="absolute inset-0 flex items-center justify-center text-harmony-white/30">
                      <FaGamepad className="mr-2" /> {category.name}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-harmony-white font-semibold mb-1">{category.name}</h3>
                    <div className="flex flex-wrap gap-2 my-2">
                      {category.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="text-xs text-harmony-white/70 bg-harmony-blue/30 px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-3 text-sm text-harmony-white/70">
                      <div>{category.viewers.toLocaleString()} viewers</div>
                      <div>{category.channels.toLocaleString()} channels</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-harmony-white/50 text-5xl mb-4">
              <FaSearch />
            </div>
            <h3 className="text-harmony-white text-xl font-medium mb-2">No categories found</h3>
            <p className="text-harmony-white/70">Try adjusting your search query.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Categories; 